import * as React from "react";
import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import {
  STATE_LABEL,
  STATE_COLOR,
  fmtDate,
  fmtThroughInclusive,
  fmtStamp,
} from "../utils/eligibilityDisplay";
import { buildTimeline } from "../utils/eligibilityRollup";
import { canReject, canConfirm, canEdit, canDelete } from "../utils/eligibilityActions";
import EligibilityRejectDialog from "./EligibilityRejectDialog";
import EligibilityConfirmDialog from "./EligibilityConfirmDialog";
import EligibilityEditDialog from "./EligibilityEditDialog";
import EligibilityDeletePeriodDialog from "./EligibilityDeletePeriodDialog";

const COLSPAN = 9;

// One franchise's eligibility history as a chronological timeline: every stored period plus
// DERIVED inactive gaps between consecutive confirmed periods. State-appropriate write actions
// live in the Actions column. `onChanged` is called after a successful write so the host container
// can reload and surface a success message.
//
// ACTION HOST: a single `activeAction` = { type, period } drives all write dialogs, so future
// slices (Confirm / Edit / Close / Rejoin) are additive — each adds a can* predicate, an action
// button, and one case in renderActiveDialog(), never a new piece of dialog state.
export default function EligibilityTimeline({ franchise, onChanged }) {
  const periods = franchise.periods || [];
  const [activeAction, setActiveAction] = useState(null);

  const completeAction = (msg) => {
    setActiveAction(null);
    if (onChanged) onChanged(msg);
  };

  const renderActiveDialog = () => {
    if (!activeAction) return null;
    switch (activeAction.type) {
      case "confirm":
        return (
          <EligibilityConfirmDialog
            open
            period={activeAction.period}
            comCode={franchise.com_code}
            franchiseName={franchise.franchise_name}
            onClose={() => setActiveAction(null)}
            onConfirmed={(msg) => completeAction(msg)}
          />
        );
      case "edit":
        return (
          <EligibilityEditDialog
            open
            period={activeAction.period}
            comCode={franchise.com_code}
            franchiseName={franchise.franchise_name}
            onClose={() => setActiveAction(null)}
            onEdited={(msg) => completeAction(msg)}
          />
        );
      case "reject":
        return (
          <EligibilityRejectDialog
            open
            period={activeAction.period}
            comCode={franchise.com_code}
            franchiseName={franchise.franchise_name}
            onClose={() => setActiveAction(null)}
            onRejected={() => completeAction("Eligibility proposal rejected.")}
          />
        );
      case "delete":
        return (
          <EligibilityDeletePeriodDialog
            open
            period={activeAction.period}
            comCode={franchise.com_code}
            franchiseName={franchise.franchise_name}
            isFinalPeriod={periods.length === 1}
            onClose={() => setActiveAction(null)}
            onDeleted={(msg) => completeAction(msg)}
          />
        );
      default:
        return null;
    }
  };

  if (periods.length === 0) {
    return (
      <Box sx={{ px: 2, py: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          No eligibility periods configured — this franchise <strong>fails open</strong> and keeps
          the current <em>LK/SI Client</em> behaviour until a period is created.
        </Typography>
      </Box>
    );
  }

  const entries = buildTimeline(periods);

  const rows = entries.map((e) => {
    if (e.type === "gap") {
      return (
        <TableRow key={`gap-${e.from}-${e.through}`}>
          <TableCell colSpan={COLSPAN} sx={{ py: 0.75 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
              Inactive gap · {fmtDate(e.from)} → {fmtThroughInclusive(e.through)}{" "}
              <Box component="span" sx={{ opacity: 0.7 }}>(derived, not stored)</Box>
            </Typography>
          </TableCell>
        </TableRow>
      );
    }

    const p = e.period;
    const throughInclusive = fmtThroughInclusive(p.enabled_through_date);
    const note = p.evidence_note || "";
    return (
      <TableRow key={p.enablement_period_id} hover>
        <TableCell>
          <Chip
            size="small"
            label={STATE_LABEL[p.eligibility_state] ?? p.eligibility_state}
            color={STATE_COLOR[p.eligibility_state] ?? "default"}
          />
        </TableCell>
        <TableCell>{fmtDate(p.enabled_from_date)}</TableCell>
        <TableCell>{throughInclusive ?? "Open-ended"}</TableCell>
        <TableCell>{p.source || "—"}</TableCell>
        <TableCell>{p.confidence || "—"}</TableCell>
        <TableCell align="center">{p.horizon_limited ? "Yes" : "No"}</TableCell>
        <TableCell>{p.reviewed_by || "—"}</TableCell>
        <TableCell sx={{ maxWidth: 260 }}>
          <Tooltip title={note || ""}>
            <Typography variant="body2" noWrap sx={{ maxWidth: 260 }}>
              {note || "—"}
            </Typography>
          </Tooltip>
        </TableCell>
        <TableCell align="right">
          {canConfirm(p) && (
            <Button
              size="small"
              color="primary"
              onClick={() => setActiveAction({ type: "confirm", period: p })}
            >
              Review &amp; Confirm…
            </Button>
          )}
          {canEdit(p) && (
            <Button
              size="small"
              color="primary"
              onClick={() => setActiveAction({ type: "edit", period: p })}
            >
              Edit
            </Button>
          )}
          {canReject(p) && (
            <Button
              size="small"
              color="error"
              onClick={() => setActiveAction({ type: "reject", period: p })}
            >
              Reject
            </Button>
          )}
          {canDelete(p) && (
            <Button
              size="small"
              color="error"
              onClick={() => setActiveAction({ type: "delete", period: p })}
            >
              Delete
            </Button>
          )}
        </TableCell>
      </TableRow>
    );
  });

  return (
    <Box sx={{ px: 2, py: 1.5 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Status</TableCell>
            <TableCell>Enabled From</TableCell>
            <TableCell>
              <Tooltip title="Shown inclusively. Stored internally as a half-open interval [from, through), so the stored 'through' is exclusive.">
                <span>Enabled Through</span>
              </Tooltip>
            </TableCell>
            <TableCell>Source</TableCell>
            <TableCell>Confidence</TableCell>
            <TableCell align="center">Horizon Limited</TableCell>
            <TableCell>Reviewed By</TableCell>
            <TableCell>Evidence Note</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>{rows}</TableBody>
      </Table>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
        Last updated {fmtStamp(franchise.lastUpdated)}.
      </Typography>

      {renderActiveDialog()}
    </Box>
  );
}
